import { Box, Flex, Text } from "@minervaproject/fds";
import { Duration, intervalToDuration } from "date-fns";
import { useCallback, useContext, useEffect, useLayoutEffect } from "react";

import { TimelineSegment } from "./timeline_segment";

import { createBreakoutSessionHelper } from "api/api_helpers";
import { FlightErrorBoundary } from "components/error_boundary";
import { createErrorToast } from "components/shared/toasts";
import { FlightAPIContext } from "contexts/flight_api_context";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import {
  PROFILE_PROPERTIES,
  USER_PICKER_PROPERTIES,
  useUsersProperties,
} from "hooks/use_user_properties";
import {
  setActiveTimelineSegmentId,
  setAllStagePanes,
  setCurrentFilter,
} from "shared/reducers/classroom";
import {
  CapabilityType,
  KeyboardShortcutSignalType,
  TimelineSegmentOpType,
  TimelineSegmentType,
} from "shared/types";
import { ClassEventTypeType } from "shared/types/class_event";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { FilterStrategyType } from "shared/utils/user_picker";
import { classroomSelectors, localSelectors } from "store";
import { setKeyboardShortcutSignal, setShowInstructorNotes } from "store/reducers/local";
import { createClassEvent, createMonitoringEvent } from "store/reducers/monitoring";
import { isBreakoutBasedOnPoll } from "utils/breakout";
import { pickUsersAndSetParticipantBarAndStage } from "utils/classroom";
import { buildStagePanesFromTimelinePanes } from "utils/stage";
import { getBreakoutForTimelineSegment } from "utils/timeline";

import "./timeline.scss";

function Timeline() {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentlyBackingUpPoll = useAppSelector(localSelectors.selectCurrentlyBackingUpPoll());
  const activeRoomId = useAppSelector(classroomSelectors.selectActiveRoomIdForCurrentUser());
  const timeline = useAppSelector(classroomSelectors.selectTimeline());
  const activeTimelineSegment = useAppSelector(classroomSelectors.selectActiveTimelineSegment());
  const users = Object.values(
    useUsersProperties(PROFILE_PROPERTIES.concat(USER_PICKER_PROPERTIES).concat(["vote"])),
  );
  const breakouts = Object.values(useAppSelector(classroomSelectors.selectBreakouts()));
  const hasActiveBreakout = !!useAppSelector(classroomSelectors.selectActiveBreakoutSessionId());
  const polls = Object.values(useAppSelector(classroomSelectors.selectPolls()));
  const pollSessions = Object.values(useAppSelector(classroomSelectors.selectPollSessions()));
  const resources = Object.values(useAppSelector(classroomSelectors.selectResources()));
  const flightAPI = useContext(FlightAPIContext);
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const keyboardShortcutSignal = useAppSelector(localSelectors.selectKeyboardShortcutSignal());
  const showInstructorNotes = useAppSelector(localSelectors.selectShowInstructorNotes());

  // For filtering
  const maxSubscribedStreams = useAppSelector(classroomSelectors.selectMaxSubscribedStreams());
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const presentVisibleUsers = Object.values(users).filter(
    (user) => user.is_present && isVisibleInClassUserIds.includes(user.id),
  );
  const { panes } = useAppSelector(classroomSelectors.selectStage());
  const isAlwaysSubscribedToUserIds = useUserIdsWithCapability(
    CapabilityType.IS_ALWAYS_SUBSCRIBED_TO,
  );
  const participantBarUserIds = useAppSelector(classroomSelectors.selectParticipantBarUserIds());

  // Returns a list of user ids in the participant bar
  const maybeApplyFilter = useCallback(
    async (segment: TimelineSegmentType) => {
      // Filtering for the participant bar is only relevant in large virtual classes
      if (segment.filter && presentVisibleUsers.length > maxSubscribedStreams && !isInPerson) {
        return pickUsersAndSetParticipantBarAndStage(
          dispatch,
          maxSubscribedStreams,
          currentUserId,
          presentVisibleUsers,
          isAlwaysSubscribedToUserIds,
          canParticipateInClassUserIds,
          participantBarUserIds,
          panes,
          segment.filter as FilterStrategyType,
          true,
        );
      }

      // Set filter even if there is no need to filter so that any existing filters are cleared with each timeline step
      dispatch(setCurrentFilter({ filterStrategy: segment.filter }));

      return presentVisibleUsers.map((user) => user.id);
    },
    [
      canParticipateInClassUserIds,
      currentUserId,
      dispatch,
      isAlwaysSubscribedToUserIds,
      isInPerson,
      maxSubscribedStreams,
      panes,
      participantBarUserIds,
      presentVisibleUsers,
    ],
  );

  const advanceTimeline = useCallback(
    async (segment: TimelineSegmentType, force = false) => {
      if (activeTimelineSegment?.id === segment.id && !force) {
        return;
      }

      switch (segment.op) {
        case TimelineSegmentOpType.Breakout: {
          const serverMilliseconds = flightAPI.getServerMilliseconds();
          const breakout = getBreakoutForTimelineSegment(segment, breakouts);
          const disabled = isBreakoutBasedOnPoll(breakout, currentlyBackingUpPoll);
          if (disabled) {
            createErrorToast(
              "Error Starting Breakout",
              `There was an error while starting the breakout. Please try again.`,
            );
            return;
          }
          if (breakout) {
            await createBreakoutSessionHelper(
              breakout,
              users,
              currentlyBackingUpPoll,
              serverMilliseconds,
              dispatch,
              forumAPI,
            );
          }
          break;
        }

        case TimelineSegmentOpType.FeatureAllGroupsPeopleAndNotes:
        case TimelineSegmentOpType.FeatureAllPreclassGroups:
          // Nothing happens here, other than secondary buttons appearing
          break;

        case TimelineSegmentOpType.Layout: {
          if (segment.panes) {
            const updatedParticipantBarUserIds = await maybeApplyFilter(segment);
            const stagePanes = await buildStagePanesFromTimelinePanes(
              segment.panes,
              users,
              updatedParticipantBarUserIds,
              polls,
              pollSessions,
              resources,
              isInPerson,
              dispatch,
              forumAPI,
            );
            window.flightLogger.log(
              `[Timeline] Updating stage panes: ${JSON.stringify(stagePanes)}`,
            );
            dispatch(setAllStagePanes({ panes: stagePanes, ownerId: currentUserId }));
          } else {
            // Shouldn't get here ever
            window.flightLogger.error("No panes for layout segment");
          }
          break;
        }

        default:
          window.flightLogger.warn(`Unknown timeline segment op ${segment.op}`);
      }
      dispatch(setActiveTimelineSegmentId({ id: segment.id }));
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.TIMELINE_SEGMENT,
          userId: currentUserId,
          contextData: { timelineSegmentId: segment.id },
        }),
      );
      dispatch(
        createClassEvent({
          type: ClassEventTypeType.TIMELINE_SEGMENT,
          userId: currentUserId,
          roomId: activeRoomId,
          data: { "timeline-segment-id": segment.id, "timeline-segment-title": segment.title },
        }),
      );
    },
    [
      activeRoomId,
      activeTimelineSegment?.id,
      breakouts,
      currentUserId,
      currentlyBackingUpPoll,
      dispatch,
      flightAPI,
      forumAPI,
      isInPerson,
      maybeApplyFilter,
      pollSessions,
      polls,
      resources,
      users,
    ],
  );

  const toggleShowInstructorNotes = useCallback(() => {
    dispatch(setShowInstructorNotes({ showInstructorNotes: !showInstructorNotes }));
  }, [dispatch, showInstructorNotes]);

  // We only have the timeline duration in seconds, but need to calculate the formatted string to display
  // the start and end times relative to the class start.
  let lastSectionEndTimeSeconds = 0;
  const formattedSectionTimes = timeline.sections.map((section) => {
    const start = intervalToDuration({ start: 0, end: lastSectionEndTimeSeconds * 1000 });
    const end = intervalToDuration({
      start: 0,
      end: lastSectionEndTimeSeconds * 1000 + section.duration_in_seconds * 1000,
    });
    const pad = (num: number) => String(num).padStart(2, "0");
    const formatInterval = (duration: Duration) =>
      (duration.hours || 0) > 0
        ? `${duration.hours}:${pad(duration.minutes || 0)}:${pad(duration.seconds || 0)}`
        : `${pad(duration.minutes || 0)}:${pad(duration.seconds || 0)}`;
    lastSectionEndTimeSeconds += section.duration_in_seconds;
    return `${formatInterval(start)}-${formatInterval(end)}`;
  });

  useEffect(() => {
    if (keyboardShortcutSignal === KeyboardShortcutSignalType.ToggleInstructorNotes) {
      toggleShowInstructorNotes();
      dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.None }));
    }
  }, [dispatch, keyboardShortcutSignal, toggleShowInstructorNotes]);

  useEffect(() => {
    if (
      ![
        KeyboardShortcutSignalType.NextTimelineSegment,
        KeyboardShortcutSignalType.PreviousTimelineSegment,
      ].includes(keyboardShortcutSignal)
    ) {
      return;
    }

    if (hasActiveBreakout || timeline.sections.length === 0) {
      dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.None }));
      return;
    }

    if (keyboardShortcutSignal === KeyboardShortcutSignalType.NextTimelineSegment) {
      if (activeTimelineSegment) {
        const segments = timeline.sections.flatMap((section) => section.segments);
        const activeSegmentIndex = segments.findIndex(
          (segment) => segment.id === activeTimelineSegment.id,
        );
        if (activeSegmentIndex < segments.length - 1) {
          const nextSegment = segments[activeSegmentIndex + 1];
          advanceTimeline(nextSegment);
        }
      } else {
        const firstSegment = timeline.sections[0].segments[0];
        advanceTimeline(firstSegment);
      }
    } else if (keyboardShortcutSignal === KeyboardShortcutSignalType.PreviousTimelineSegment) {
      if (activeTimelineSegment) {
        const segments = timeline.sections.flatMap((section) => section.segments);
        const activeSegmentIndex = segments.findIndex(
          (segment) => segment.id === activeTimelineSegment.id,
        );
        if (activeSegmentIndex > 0) {
          const previousSegment = segments[activeSegmentIndex - 1];
          advanceTimeline(previousSegment);
        }
      }
    }

    dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.None }));
  }, [
    advanceTimeline,
    dispatch,
    hasActiveBreakout,
    activeTimelineSegment,
    keyboardShortcutSignal,
    timeline.sections,
  ]);

  useLayoutEffect(() => {
    const activeTimelineSegmentElement = window.document.getElementById(
      `timeline-segment-${activeTimelineSegment?.id}`,
    );
    if (activeTimelineSegment && activeTimelineSegmentElement) {
      // Passing false prevents the entire classroom from shifting up as well
      activeTimelineSegmentElement.scrollIntoView(false);
    }
    // We only want to do this on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex direction="column" className="sidebar-width">
      <Text className="body-l flex-none p2" kind="h2">
        Class Timeline
      </Text>
      <Box className="vertical-scroll flex-auto pb2 px2">
        {timeline.sections.map((section, index) => (
          <Flex className="mt2" key={section.id} direction="column" data-testid="timeline-section">
            <Flex className="items-center justify-between">
              <Text kind="body-xs" color="white" bold>
                {section.title}
              </Text>
              <Text kind="body-xs" color="white">
                {formattedSectionTimes[index]}
              </Text>
            </Flex>
            {section.segments.map((segment) => (
              <FlightErrorBoundary
                key={segment.id}
                text="This timeline segment cannot be displayed"
                margin="4px 8px 4px"
              >
                <TimelineSegment
                  segment={segment}
                  isActive={activeTimelineSegment?.id === segment.id}
                  advanceTimeline={advanceTimeline}
                  toggleShowInstructorNotes={toggleShowInstructorNotes}
                />
              </FlightErrorBoundary>
            ))}
          </Flex>
        ))}
      </Box>
    </Flex>
  );
}

export { Timeline };
